import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MsalService } from '@azure/msal-angular';
import {
  LegendItemVisualArgs,
  SeriesLabelsContentArgs
} from '@progress/kendo-angular-charts';
import { map } from 'rxjs/operators';
import { groupBy, GroupResult } from '@progress/kendo-data-query';
import { addDays } from '@progress/kendo-date-math';
import {
  GetCustomerRankingResult,
  GetProductRankingResult,
  GetSalesFunnelItems,
  KPI,
  KPIService,
  ReportingService
} from '@api-reports-client';

import { AppSettingsService } from '../../shared/services/app';
import { drawing, geometry, Group, Rect, Text } from '@progress/kendo-drawing';
import { logoutRequest } from '../../auth/auth-mode';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  providers: [DatePipe],
  standalone: false
})
export class HomeComponent implements OnInit {
  dayShift: string;
  currencyUid: string;

  kpiProfitLoss: number;
  kpiGrossProfit: KPI[] = [];
  salesFunnel: GetSalesFunnelItems[];
  salesReportRolling: GroupResult[] | null = null;
  salesReportMonthly: GroupResult[] | null = null;
  salesReportMode: 'rolling' | 'monthly' = 'rolling';

  get activeSalesReport(): GroupResult[] | null {
    return this.salesReportMode === 'rolling' ? this.salesReportRolling : this.salesReportMonthly;
  }
  customerRanking: GetCustomerRankingResult = {};
  numberOfCustomersByRanking = 12;
  productRanking: GetProductRankingResult = {};
  pieChart = [];
  private todayDate = new Date();
  chartDefaultV4Colors: string[] = [
    '#0e5a7e',
    '#2185b4',
    '#319fd2',
    '#3eaee2',
    '#166f99'
  ];

  public seriesColors: string[] = this.chartDefaultV4Colors;

  constructor(
    private msalService: MsalService,
    public datepipe: DatePipe,
    private reportingService: ReportingService,
    private kpiService: KPIService,
    protected appSettingsService: AppSettingsService
  ) {
    this.currencyUid =
      this.appSettingsService.currenciesSettings.baseCurrency ?? 'USD';
  }

  async ngOnInit(): Promise<void> {
    this.dayShift = this.getGreetingTime(
      this.datepipe.transform(new Date(), 'H')
    );
    this.getKPIProfitAndLoss();
    this.getKPIGrossProfit();
    this.getReportSalesFunnel();
    this.getSalesReports();
    await this.getReportCustomerRanking();
    this.getReportProductRanking();

    this.generatePieChart();
  }

  private generatePieChart(): void {
    const sales = [],
      profits = [];
    this.customerRanking.pieChartSalesRank.forEach((item) => {
      sales.push({
        customerName: item.customerName,
        customerUid: item.customerUid,
        percentage: item.salesPercentage
      });
      profits.push({
        customerName: item.customerName,
        customerUid: item.customerUid,
        percentage: item.profitPercentage
      });
    });
    this.pieChart.push({
      name: 'Sales',
      data: sales
    });
    this.pieChart.push({
      name: 'Profit',
      data: profits
    });
  }

  public outerPieChartLabelContent(e: SeriesLabelsContentArgs): string {
    return `${e.dataItem.customerName}\n${(e.dataItem.percentage * 100).toFixed(2)}%`;
  }

  public pieChartTooltipValue(value: number): string {
    return (value * 100).toFixed(2);
  }

  async getKPIProfitAndLoss() {
    this.kpiProfitLoss = await this.kpiService
      .kPIPnLGet(null, addDays(this.todayDate, -30), this.todayDate)
      .pipe(map((x) => x.pnLResult))
      .toPromise();
  }

  async getKPIGrossProfit() {
    this.kpiGrossProfit = await this.kpiService
      .getGrossProfitability(30, 3)
      .pipe(
        map((response) =>
          response.kpIs.map((kpi) => {
            return {
              ...kpi,
              percentageChange: kpi.percentageChange * 100
            };
          })
        )
      )
      .toPromise();
  }

  async getReportSalesFunnel() {
    this.salesFunnel = await this.reportingService
      .getSalesFunnel()
      .pipe(map((x) => x.items))
      .toPromise();
  }

  async getReportCustomerRanking() {
    this.customerRanking = await this.reportingService
      .getCustomerRanking(this.numberOfCustomersByRanking, 90)
      .toPromise();
  }

  async getReportProductRanking() {
    this.productRanking = await this.reportingService
      .getProductRanking(5, 90)
      .toPromise();
  }

  async getSalesReports() {
    const result = await this.reportingService
      .getSalesReport()
      .toPromise() as any;

    this.salesReportRolling = groupBy(result.rollingItems ?? [], [{ field: 'name' }]) as GroupResult[];
    this.salesReportMonthly = groupBy(result.monthlyItems ?? [], [{ field: 'name' }]) as GroupResult[];
  }

  toggleSalesReportMode(mode: 'rolling' | 'monthly'): void {
    this.salesReportMode = mode;
  }

  getGreetingTime(hour: any) {
    let greeting = '';

    if (!hour) {
      return;
    } // if we can't find a valid or filled moment, we return.

    const splitAfternoon = 12; // 24hr time to split the afternoon
    const splitEvening = 17; // 24hr time to split the evening
    const currentHour = hour;

    if (currentHour >= splitAfternoon && currentHour <= splitEvening) {
      greeting = 'afternoon';
    } else if (currentHour >= splitEvening) {
      greeting = 'evening';
    } else {
      greeting = 'morning';
    }

    return greeting;
  }

  signOut() {
    this.msalService.logout(logoutRequest(this.msalService.instance));
  }

  public salesReportLegendMarkerVisual = (
    args: LegendItemVisualArgs
  ): drawing.Element => {
    const options: any = args.options || {};
    const markers = options.markers || {};
    const labels = options.labels || {};

    const color =
      markers.border?.color ||
      markers.background ||
      args.series?.color ||
      '#0e5a7e';

    const labelText = options.text || args.series?.name || '';

    const labelColor = labels.color || '#444';

    const group = new Group();

    const size = 18;
    const rectGeometry = new geometry.Rect([0, 0], [size, size]);

    const square = new Rect(rectGeometry, {
      fill: { color },
      stroke: { color, width: 0 }
    });

    // Vertically center the text relative to the square (approximate text height ~12px)
    const labelY = (size - 12) / 2;
    const label = new Text(labelText, [size + 6, labelY], {
      fill: { color: labelColor }
    });

    group.append(square);
    group.append(label);
    return group;
  };
}
